import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../lib/lit.js';
import { router } from '../../lib/router.js';

class ChannelAppsView extends BaseComponent {
    static properties = {
        _channelApps: { type: Array, state: true },
        _catalog: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _view: { type: String, state: true },
        _formData: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
        _confirmDelete: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
    `;

    constructor() {
        super();
        this._channelApps = [];
        this._catalog = [];
        this._loading = true;
        this._error = null;
        this._view = 'list';
        this._formData = { discord_channel_id: '', repo: '' };
        this._saving = false;
        this._confirmDelete = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        this._unsubscribeRouter = router.subscribe((state) => {
            if (state.path === '/channel-apps/new') {
                this._view = 'create';
                this._formData = { discord_channel_id: '', repo: '' };
            } else {
                this._view = 'list';
            }
        });
        this._loadData();
        if (window.location.pathname === '/channel-apps/new') {
            this._view = 'create';
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
    }

    async _loadData() {
        this._loading = true;
        this._error = null;
        try {
            const [channelApps, catalog] = await Promise.all([
                api.get('/api/channel-apps'),
                api.get('/api/apps'),
            ]);
            this._channelApps = channelApps;
            this._catalog = catalog;
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    _appName(repo) {
        const app = this._catalog.find((a) => a.Repo === repo);
        return app ? app.Name : repo;
    }

    async _submitCreate(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            await api.post('/api/channel-apps', this._formData);
            await this._loadData();
            router.navigate('/channel-apps');
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _deleteChannelApp(id) {
        this._error = null;
        try {
            await api.delete(`/api/channel-apps/${id}`);
            this._confirmDelete = null;
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        }
    }

    _updateField(field, value) {
        this._formData = { ...this._formData, [field]: value };
    }

    render() {
        if (this._view === 'create') return this._renderForm();
        return this._renderList();
    }

    _renderList() {
        return html`
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Channel Apps</h5>
                <a href="/channel-apps/new" class="btn btn-primary btn-sm">
                    <i class="bi bi-plus-lg"></i> Add Mapping
                </a>
            </div>

            <p class="text-muted" style="font-size: 0.875rem;">
                Restrict which apps <code>/d3po</code> offers in a channel. A channel with
                no mappings offers every app; add mappings to limit it to specific apps.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            ${
                this._loading
                    ? html`
                <div class="text-center py-4">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                </div>
            `
                    : html`
                ${
                    this._channelApps.length === 0
                        ? html`
                    <div class="text-muted text-center py-4">No channel apps configured.</div>
                `
                        : html`
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Channel ID</th>
                                <th>App</th>
                                <th style="width: 80px;"></th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._channelApps.map(
                                (ca) => html`
                                <tr>
                                    <td><code>${ca.DiscordChannelID}</code></td>
                                    <td>${this._appName(ca.Repo)} <span class="text-muted">(<code>${ca.Repo}</code>)</span></td>
                                    <td class="text-end">
                                        ${
                                            this._confirmDelete === ca.ID
                                                ? html`
                                            <button class="btn btn-danger btn-sm" @click="${() => this._deleteChannelApp(ca.ID)}">
                                                Confirm
                                            </button>
                                        `
                                                : html`
                                            <button class="btn btn-outline-danger btn-sm" @click="${() => (this._confirmDelete = ca.ID)}">
                                                <i class="bi bi-trash"></i>
                                            </button>
                                        `
                                        }
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                }
            `
            }
        `;
    }

    _renderForm() {
        return html`
            <div class="d-flex align-items-center gap-2 mb-3">
                <a href="/channel-apps" class="btn btn-outline-secondary btn-sm">
                    <i class="bi bi-arrow-left"></i>
                </a>
                <h5 class="mb-0">Add Channel App</h5>
            </div>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            <form @submit="${(e) => this._submitCreate(e)}" style="max-width: 500px;">
                <div class="mb-3">
                    <label class="form-label">Discord Channel ID</label>
                    <input type="text" class="form-control" placeholder="e.g. 1234567890"
                           .value="${this._formData.discord_channel_id}"
                           @input="${(e) => this._updateField('discord_channel_id', e.target.value)}"
                           required>
                    <div class="form-text">Enable Developer Mode in Discord settings, then right-click the channel and Copy Channel ID</div>
                </div>

                <div class="mb-3">
                    <label class="form-label">App</label>
                    <select class="form-select"
                            .value="${this._formData.repo}"
                            @change="${(e) => this._updateField('repo', e.target.value)}"
                            required>
                        <option value="" disabled ?selected="${!this._formData.repo}">Select an app…</option>
                        ${this._catalog.map(
                            (app) => html`
                            <option value="${app.Repo}" ?selected="${this._formData.repo === app.Repo}">
                                ${app.Name} (${app.Repo})
                            </option>
                        `,
                        )}
                    </select>
                </div>

                <div class="d-flex gap-2">
                    <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                        ${this._saving ? html`<span class="spinner-border spinner-border-sm"></span>` : 'Save'}
                    </button>
                    <a href="/channel-apps" class="btn btn-outline-secondary">Cancel</a>
                </div>
            </form>
        `;
    }
}

customElements.define('channel-apps-view', ChannelAppsView);
